import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { MS_PER_SECOND, SESSION_HEARTBEAT_MS, SESSION_RECHECK_MS, USER_ACTIVE_WINDOW_MS } from '~constants/session';

import { getLastForegroundRequestAt, getLastInputAt } from '~libs/sessionActivity';
import { describeDuration, describeSessionLimit, formatCountdown } from '~libs/sessionCountdown';
import { requirePasswordConfirmation, resolveExpiredSession } from '~libs/sessionExpiry';
import { usesSsoSignIn } from '~libs/signInRoute';

import { Button, ErrorResponse, Modal } from '~components';

import { extendSession } from '~main/actions/session';

import { setReauthPrompt } from '~main/reducers/login';

import { selectReauthPrompt, selectSession } from '~main/selectors/login';

const TICK_MS = MS_PER_SECOND;
// The absolute limit binds when it lands within a second of the idle deadline.
const ABSOLUTE_BINDS_TOLERANCE_MS = MS_PER_SECOND;
// The deadline is recomputed from relative seconds on every response, so it drifts by milliseconds. A reauth
// moves it by hours, far beyond this.
const DISMISSED_DEADLINE_TOLERANCE_MS = 5 * MS_PER_SECOND;
const EXTEND_FAILED_MESSAGE = 'We could not extend your session. Please try again.';
const noop = () => undefined;

/**
 * Warns before the session ends and keeps it alive while the user is working. It is a static-backdrop modal
 * that opens inside the warning window, with two shapes:
 * - idle: "Stay signed in" slides the session, "Sign out" leaves;
 * - absolute (the 12 hour limit): "Confirm password now" opens the reauth modal early, "Later" dismisses.
 * At zero it never signs the user out by itself: idle asks the API first, and the absolute limit raises the
 * password prompt. It also runs the typing heartbeat. Users who signed in through SSO have no TAV password, so
 * the absolute shape asks them to sign in with their organisation instead (TAV-3402).
 */
const SessionExpiryOverlay: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector(selectSession);
  const reauthPrompt = useAppSelector(selectReauthPrompt);

  const [now, setNow] = useState(() => Date.now());
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  // "Later" hides the absolute-limit notice until the limit itself, keyed by the deadline it was pressed for.
  const [dismissedAbsoluteAt, setDismissedAbsoluteAt] = useState<number | null>(null);

  // The tick reads the latest values through refs, so the interval is created once rather than per render.
  const latest = useRef({ session, reauthPrompt });
  latest.current = { session, reauthPrompt };
  const handledExpiryAt = useRef<number | null>(null);
  const heartbeatInFlight = useRef(false);
  const recheckTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const tick = () => {
      const current = Date.now();
      setNow(current);

      const { session: state, reauthPrompt: prompt } = latest.current;
      if (!state) return;

      // Heartbeat: someone is typing but nothing has gone to the API for a minute. Suppressed while the
      // password prompt is up, because the API would only answer reauth-required.
      if (
        prompt !== 'required' &&
        !heartbeatInFlight.current &&
        current < state.expiresAt &&
        current - getLastInputAt() <= USER_ACTIVE_WINDOW_MS &&
        current - getLastForegroundRequestAt() >= SESSION_HEARTBEAT_MS
      ) {
        heartbeatInFlight.current = true;
        dispatch(extendSession()).finally(() => {
          heartbeatInFlight.current = false;
        });
      }

      // Zero. Handled once per deadline; a slide moves the deadline and re-arms this.
      if (prompt === 'none' && current >= state.expiresAt && handledExpiryAt.current !== state.expiresAt) {
        handledExpiryAt.current = state.expiresAt;
        const absoluteBinds = state.absoluteExpiresAt <= state.expiresAt + ABSOLUTE_BINDS_TOLERANCE_MS;
        if (absoluteBinds && state.reauthGraceSeconds > 0) {
          requirePasswordConfirmation(dispatch);
        } else {
          const deadline = state.expiresAt;
          resolveExpiredSession(dispatch).then((result) => {
            // The API could not be asked: re-arm this deadline so a later tick asks again.
            if (result !== 'unavailable') return;
            recheckTimer.current = setTimeout(() => {
              if (handledExpiryAt.current === deadline) handledExpiryAt.current = null;
            }, SESSION_RECHECK_MS);
          });
        }
      }
    };

    const timer = setInterval(tick, TICK_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(recheckTimer.current);
    };
  }, [dispatch]);

  const warningMs = session ? session.warningSeconds * MS_PER_SECOND : 0;
  const remainingMs = session ? session.expiresAt - now : 0;
  const absoluteBinds = session ? session.absoluteExpiresAt <= session.expiresAt + ABSOLUTE_BINDS_TOLERANCE_MS : false;
  const dismissed =
    absoluteBinds &&
    session !== null &&
    dismissedAbsoluteAt !== null &&
    Math.abs(dismissedAbsoluteAt - session.absoluteExpiresAt) < DISMISSED_DEADLINE_TOLERANCE_MS;
  const visible =
    Boolean(session) && reauthPrompt === 'none' && remainingMs > 0 && remainingMs <= warningMs && !dismissed;

  const onStay = async () => {
    setExtending(true);
    setExtendError(null);
    const result = await dispatch(extendSession());
    if (extendSession.rejected.match(result)) setExtendError(EXTEND_FAILED_MESSAGE);
    setExtending(false);
  };

  const onConfirmNow = () => {
    dispatch(setReauthPrompt('early'));
  };

  const onLater = () => {
    if (session) setDismissedAbsoluteAt(session.absoluteExpiresAt);
  };

  const onSignOut = () => {
    navigate('/logout');
  };

  const countdown = formatCountdown(remainingMs);
  const heading = absoluteBinds ? 'Session limit reached' : 'Are you still there?';
  const isSso = absoluteBinds && usesSsoSignIn();
  const limit = session ? describeSessionLimit(session.absoluteTimeoutSeconds) : '';
  const ssoMessage = `You have reached the ${limit} session limit. Sign in with your organisation to keep working. Nothing on this page will be lost.`;
  const passwordAnnouncement = absoluteBinds
    ? 'Your session limit is about to be reached. Confirm your password to keep your session active.'
    : 'You will be signed out soon because of inactivity.';
  const announcement = isSso ? ssoMessage : passwordAnnouncement;

  return (
    <Modal
      isOpen={visible}
      setIsOpen={noop}
      header={heading}
      disableClose
      backdrop="static"
      keyboard={false}
      centered
      footer={
        absoluteBinds ? (
          <>
            <Button color="success" onClick={onConfirmNow} dataCy="session-overlay-confirm-button">
              {isSso ? 'Sign in now' : 'Confirm password now'}
            </Button>
            <Button color="secondary" onClick={onLater} dataCy="session-overlay-later-button">
              Later
            </Button>
          </>
        ) : (
          <>
            <Button color="success" loading={extending} onClick={onStay} dataCy="session-overlay-stay-button">
              Stay signed in
            </Button>
            <Button color="secondary" disabled={extending} onClick={onSignOut} dataCy="session-overlay-signout-button">
              Sign out
            </Button>
          </>
        )
      }
    >
      <span className="visually-hidden" role="status">
        {announcement}
      </span>
      {isSso && <p>{ssoMessage}</p>}
      {absoluteBinds && !isSso && (
        <p>
          You have reached the {session ? describeSessionLimit(session.absoluteTimeoutSeconds) : ''} session limit.
          Confirm your password to keep your session active. Nothing on this page will be lost.
        </p>
      )}
      {!absoluteBinds && (
        <p>
          You have been inactive for a while. For your security, sessions end after{' '}
          {session ? describeDuration(session.idleTimeoutSeconds) : ''} without activity.
        </p>
      )}
      <p className="mb-0">
        Time remaining:{' '}
        <strong role="timer" aria-live="off" data-cy="session-overlay-countdown">
          {countdown}
        </strong>
      </p>
      <ErrorResponse message={extendError} dataCy="session-overlay-error" />
    </Modal>
  );
};

export default SessionExpiryOverlay;
